// Overlays del design-system propio: Modal (Radix Dialog con header + tamaños), ConfirmDialog
// (Modal de confirmación con acción destructiva/loading) y ActionMenu (menú "⋮" declarativo que
// filtra items por permiso/scope vía usePermissions y se oculta si no queda ninguno visible).
import { useEffect, useRef, type ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from './forms';
import { usePermissions } from './permissions';

const MODAL_SIZE: Record<string, string> = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
};

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: ReactNode;
    description?: ReactNode;
    /** Acción/decorador al lado del título (ej. un Badge "Solo lectura"). */
    titleAction?: ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    children: ReactNode;
    className?: string;
    /** `false` = solo se cierra con un botón explícito: Esc y el clic fuera no hacen nada. */
    dismissible?: boolean;
    /** `false` = no bloquea la página ni atrapa el foco; para SDKs que montan su iframe en el body. */
    modal?: boolean;
}

export function Modal({
    isOpen,
    onClose,
    title,
    description,
    titleAction,
    size = 'md',
    children,
    className,
    dismissible = true,
    modal = true,
}: ModalProps) {
    // El Modal se usa CONTROLADO, sin `DialogTrigger`: Radix no tiene a quién devolverle el foco al
    // cerrar y lo deja en el `body`. Se recuerda el último control enfocado fuera de un diálogo.
    const openerRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        const remember = (event: FocusEvent) => {
            const target = event.target as HTMLElement | null;
            if (target === null || target.closest('[role="dialog"]') !== null) return;
            openerRef.current = target;
        };
        document.addEventListener('focusin', remember);
        return () => {
            document.removeEventListener('focusin', remember);
        };
    }, []);

    return (
        <Dialog
            modal={modal}
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <DialogContent
                className={cn(MODAL_SIZE[size], className)}
                onEscapeKeyDown={(event) => {
                    if (!dismissible) event.preventDefault();
                }}
                onInteractOutside={(event) => {
                    if (!dismissible) event.preventDefault();
                }}
                onCloseAutoFocus={(event) => {
                    const opener = openerRef.current;
                    if (!opener?.isConnected) return;
                    event.preventDefault();
                    opener.focus();
                }}
            >
                {title || description || titleAction ? (
                    <DialogHeader>
                        {title || titleAction ? (
                            <div className="flex items-center justify-between gap-3">
                                {title ? <DialogTitle>{title}</DialogTitle> : <span />}
                                {titleAction}
                            </div>
                        ) : null}
                        {description ? <DialogDescription>{description}</DialogDescription> : null}
                    </DialogHeader>
                ) : null}
                {children}
            </DialogContent>
        </Dialog>
    );
}

export interface ConfirmDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: ReactNode;
    description?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    loading?: boolean;
    /** Deshabilita confirmar mientras falte algo que el diálogo pide. */
    confirmDisabled?: boolean;
    onConfirm: () => void;
    /**
     * Contenido entre el encabezado y los botones — un campo que la confirmación pide, un aviso de
     * error. Va fuera de `description` porque esa es la descripción accesible del diálogo: meterle
     * un control se lo lee al usuario de lector de pantalla como parte del texto.
     */
    children?: ReactNode;
}

export function ConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    destructive = false,
    loading = false,
    confirmDisabled = false,
    onConfirm,
    children,
}: ConfirmDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {description ? <DialogDescription>{description}</DialogDescription> : null}
                </DialogHeader>
                {children}
                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                        disabled={loading}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        variant={destructive ? 'destructive' : 'default'}
                        onClick={onConfirm}
                        isLoading={loading}
                        disabled={confirmDisabled}
                    >
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export interface ActionMenuItem {
    label: string;
    onSelect?: () => void;
    permission?: string;
    scope?: string;
    scopeRef?: string;
    icon?: ReactNode;
    destructive?: boolean;
    disabled?: boolean;
}

export interface ActionMenuProps {
    items: ActionMenuItem[];
    align?: 'start' | 'center' | 'end';
    label?: string;
}

export function ActionMenu({ items, align = 'end', label = 'Acciones' }: ActionMenuProps) {
    const { hasPermission, hasScopeAccess } = usePermissions();
    const visible = items.filter((item) => {
        if (item.permission && !hasPermission(item.permission)) return false;
        if (item.scope && !hasScopeAccess(item.scope, item.scopeRef)) return false;
        return true;
    });
    if (visible.length === 0) return null;
    // modal={false}: el menú de fila no necesita el pointer-lock de Radix; evita el page-lock que
    // documentaba DEC-009 / TD-WEBAPP-018 cuando la fila se desmonta tras la mutación.
    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    aria-label={label}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <MoreVertical className="size-4" aria-hidden />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={align}>
                {visible.map((item) => (
                    <DropdownMenuItem
                        key={item.label}
                        disabled={item.disabled}
                        onSelect={item.onSelect}
                        className={cn(
                            'gap-2',
                            item.destructive && 'text-destructive focus:text-destructive',
                        )}
                    >
                        {item.icon}
                        {item.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
